import { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
} from "firebase/firestore";
import { db } from "../../firebase";
import type { FeaturedCarousel, Product } from "../../types";
import { useCategories } from "../../hooks/useCategories";
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Input,
  Select,
  IconButton,
  useToast,
  SimpleGrid,
  Tag,
  TagLabel,
  TagCloseButton,
  Flex,
} from "@chakra-ui/react";
import { DeleteIcon, AddIcon } from "@chakra-ui/icons";

const AdminFeaturedCarousels = () => {
  const { categories } = useCategories();
  const [carousels, setCarousels] = useState<FeaturedCarousel[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [newTitle, setNewTitle] = useState("");
  const [productPicks, setProductPicks] = useState<Record<string, string>>({});
  const toast = useToast();

  const fetchAll = async () => {
    const [carouselsSnap, productsSnap] = await Promise.all([
      getDocs(query(collection(db, "featuredCarousels"), orderBy("order", "asc"))),
      getDocs(collection(db, "products")),
    ]);
    setCarousels(carouselsSnap.docs.map((d) => ({ id: d.id, ...d.data() })) as FeaturedCarousel[]);
    setProducts(productsSnap.docs.map((d) => ({ id: d.id, ...d.data() })) as Product[]);
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const handleCreate = async () => {
    if (!newTitle.trim()) return;
    await addDoc(collection(db, "featuredCarousels"), {
      title: newTitle.trim(),
      productIds: [],
      order: carousels.length,
      active: true,
    });
    setNewTitle("");
    toast({ title: "Carousel created", status: "success", duration: 3000, isClosable: true });
    fetchAll();
  };

  const handleSetCategory = async (carousel: FeaturedCarousel, categorySlug: string) => {
    await updateDoc(doc(db, "featuredCarousels", carousel.id), {
      categorySlug: categorySlug || null,
    });
    fetchAll();
  };

  const handleAddProduct = async (carousel: FeaturedCarousel) => {
    const productId = productPicks[carousel.id];
    if (!productId || carousel.productIds.includes(productId)) return;
    await updateDoc(doc(db, "featuredCarousels", carousel.id), {
      productIds: [...carousel.productIds, productId],
    });
    fetchAll();
  };

  const handleRemoveProduct = async (carousel: FeaturedCarousel, productId: string) => {
    await updateDoc(doc(db, "featuredCarousels", carousel.id), {
      productIds: carousel.productIds.filter((id) => id !== productId),
    });
    fetchAll();
  };

  const handleDelete = async (carousel: FeaturedCarousel) => {
    await deleteDoc(doc(db, "featuredCarousels", carousel.id));
    fetchAll();
  };

  const productName = (id: string) => products.find((p) => p.id === id)?.name ?? id;

  return (
    <VStack spacing={8} align="stretch">
      <HStack justify="space-between" wrap="wrap">
        <Heading fontSize="22px" color="rs.text">
          Featured Carousels
        </Heading>
        <HStack>
          <Input
            placeholder="New carousel title (e.g. Blackmagic Deals)"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            w="280px"
          />
          <Button colorScheme="red" leftIcon={<AddIcon />} onClick={handleCreate}>
            Create
          </Button>
        </HStack>
      </HStack>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
        {carousels.map((carousel) => (
          <Box key={carousel.id} bg="rs.surface" border="1px solid" borderColor="rs.border" borderRadius="10px" p={5}>
            <HStack justify="space-between" mb={3}>
              <Text fontWeight="600" color="rs.text">
                {carousel.title}
              </Text>
              <IconButton aria-label="Delete carousel" icon={<DeleteIcon />} size="sm" onClick={() => handleDelete(carousel)} />
            </HStack>

            <Text fontSize="11px" fontWeight="600" color="rs.textMuted" mb={2}>
              Scope (optional — leave "Homepage only" to show on Home)
            </Text>
            <Select
              size="sm"
              mb={4}
              value={carousel.categorySlug ?? ""}
              onChange={(e) => handleSetCategory(carousel, e.target.value)}
            >
              <option value="">Homepage only</option>
              {categories.map((c) => (
                <option key={c.id} value={c.slug}>
                  {c.name}
                </option>
              ))}
            </Select>

            <Text fontSize="11px" fontWeight="600" color="rs.textMuted" mb={2}>
              Products
            </Text>
            <Flex wrap="wrap" gap={2} mb={3}>
              {carousel.productIds.map((id) => (
                <Tag key={id} size="sm" colorScheme="red" variant="outline">
                  <TagLabel>{productName(id)}</TagLabel>
                  <TagCloseButton onClick={() => handleRemoveProduct(carousel, id)} />
                </Tag>
              ))}
              {carousel.productIds.length === 0 && (
                <Text fontSize="12px" color="rs.textDim">
                  No products yet
                </Text>
              )}
            </Flex>
            <HStack>
              <Select
                size="sm"
                placeholder="Select product to add"
                value={productPicks[carousel.id] ?? ""}
                onChange={(e) => setProductPicks({ ...productPicks, [carousel.id]: e.target.value })}
              >
                {products.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </Select>
              <Button size="sm" onClick={() => handleAddProduct(carousel)}>
                Add
              </Button>
            </HStack>
          </Box>
        ))}
      </SimpleGrid>

      {carousels.length === 0 && <Text color="rs.textMuted">No featured carousels yet — create one above.</Text>}
    </VStack>
  );
};

export default AdminFeaturedCarousels;
